import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Card,
    Form,
    Input,
    Select,
    DatePicker,
    Button,
    Row,
    Col,
    Typography,
    Table,
    message,
    Space,
    Divider,
    Alert,
    Spin,
    Modal
} from 'antd';
import {
    SaveOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { formatMoney } from '../../utils/currency';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;
const { Option } = Select;

const ElektrikFaturaIslemPage = () => {
    const { id } = useParams();
    const { darkMode } = useTheme();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [contractData, setContractData] = useState(null);
    const [calculatedAmount, setCalculatedAmount] = useState(0);
    const [inflationData, setInflationData] = useState(null);
    const [error, setError] = useState(null);
    const [bankCode, setBankCode] = useState('');
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
    });

    const companyOptions = [
        { value: 1, label: 'ATM Yönetim' },
        { value: 2, label: 'MASEF' },
        { value: 3, label: 'ATM Kiralama' }
    ];

    const invoiceTypeOptions = [
        { value: 8, label: 'Elektrik' },
        { value: 10, label: 'Temizlik' }
    ];

    const locationStatusOptions = {
        0: 'Pasif / Silinmiş',
        1: 'Pasif / Aday',
        2: 'Kurulum Bekliyor',
        3: 'Aktif',
        4: 'Pasif / Kaldırılmış',
        5: 'Pasif / Deviredilmiş',
        6: 'Pasif / Keşif',
        99: 'Offsite'
    };

    const contractSignatureStatusOptions = {
        1: 'İmzalandı',
        2: 'Onaylandı',
        6: 'Geçici Onay'
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const mParam = urlParams.get('m') ? parseInt(urlParams.get('m'), 10) : new Date().getMonth() + 1;
        const yParam = urlParams.get('y') ? parseInt(urlParams.get('y'), 10) : new Date().getFullYear();

        setFilters({
            month: mParam,
            year: yParam
        });

        if (id) {
            loadData(id, mParam, yParam);
        } else {
            setError('ID parametresi bulunamadı. Lütfen geçerli bir kayıt seçin.');
        }
    }, [id]);

    const loadData = async (id, m, y) => {
        setLoading(true);
        setError(null);

        try {
            const requestData = {
                params: {
                    id: id,
                    year: y || new Date().getFullYear(),
                    month: m || new Date().getMonth() + 1,
                    prev_year: (y || new Date().getFullYear()) - 1
                }
            };

            const response = await fetch(`${API_BASE_URL}/list/execute-query/elektrik-fatura-detay`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error:', errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const result = await response.json();   
            if (!result || (Array.isArray(result) && result.length === 0)) {
                throw new Error('Boş veri döndü - API response is empty or null');
            }                        
            setBankCode(result.banka_kabin_no || '');

            const lastInvoiceDate = result.son_fatura_tarihi;
            const contractEndDate = result['Söz. Bit. Tar.'];
            
            let startDate, endDate, faturaSuresi;
            
            if (lastInvoiceDate) {
                startDate = dayjs(lastInvoiceDate).add(1, 'month');
                endDate = dayjs(lastInvoiceDate).add(1, 'year').add(1, 'month');
                
                if (contractEndDate) {
                    const contractEnd = dayjs(contractEndDate);
                    if (contractEnd.isValid() && endDate.isAfter(contractEnd)) {
                        endDate = contractEnd;
                    }
                }
                
                faturaSuresi = Math.abs(endDate.diff(startDate, 'day'));
                
                if (faturaSuresi === 364 || faturaSuresi === 366) {
                    faturaSuresi = 365;
                }
            } else {
                startDate = null;
                endDate = null;
                faturaSuresi = 365;
            }

            const electricityAmount2019 = Number(result['2019_elektrik'] || 0);
            const invoiceAmount = electricityAmount2019 * (faturaSuresi / 365);
            setCalculatedAmount(invoiceAmount);

            if (result.sozlesme_id) {
                try {
                    const contractReq = {
                        params: {
                            id: result.sozlesme_id
                        }
                    };
                    const contractRes = await fetch(`${API_BASE_URL}/list/execute-query/sozlesme-detay`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(contractReq)
                    });
                    
                    if (contractRes.ok) {
                        const contractResult = await contractRes.json();
                        setContractData(contractResult);
                    }
                } catch (contractError) {
                    console.error('Contract data load error:', contractError);
                }
            }

            const nextData = {
                ...result,
                calculatedAmount: invoiceAmount,
                faturaSuresi: faturaSuresi,
                tip: 'elektrik'
            };
            setData(nextData);

            form.setFieldsValue({
                donemBaslangic: startDate && startDate.isValid() ? startDate : null,
                donemBitis: endDate && endDate.isValid() ? endDate : null,
                faturaTuru: 8, 
                firma: result?.firma || 1,
                fatura_tutar: invoiceAmount || 0,
                bankaKodu: bankCode
            });

        } catch (err) {
            console.error('Data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => formatMoney(amount);

    const formatDate = (date) => {
        return dayjs(date).format('DD.MM.YYYY');
    };

    const calculateDateDifference = (startDate, endDate) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diff = end.diff(start, 'day');
        
        const years = Math.floor(diff / 365);
        const months = Math.floor((diff % 365) / 30);
        const days = diff % 30;
        
        let result = '';
        if (years > 0) result += `${years} yıl `;
        if (months > 0) result += `${months} ay `;
        if (days > 0) result += `${days} gün`;
        
        return result.trim();
    };

    // 26.10 düzeltmesi (F2/F5 banka): talep /form/submit-form ile doğrudan INSERT ediliyordu; mükerrer
    // kontrolü yoktu ve banka=bsk.banka (aktif bsk'lerin çoğunda 0) yazılıyordu. Artık
    // /contracts/bank-invoice/request: lokasyon/kabin/banka bsk'den (banka = sözleşme bankası) çözülür,
    // aynı dönemde geçerli elektrik/temizlik talebi varsa 409 + çakışanlar, yalnız açık onayla force.
    // 27.10: 409'lar kodlu (mukerrer / imza); onaylanan kod 'onaylar'a eklenip yeniden gönderilir.
    const handleSubmit = async (values, onaylar = []) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/bank-invoice/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    bsk_id: Number(data.bsk_id || id),
                    firma: values.firma,
                    donem_baslangic: values.donemBaslangic.format('YYYY-MM-DD'),
                    donem_bitis: values.donemBitis.format('YYYY-MM-DD'),
                    fatura_tutar: parseFloat(values.fatura_tutar) || calculatedAmount || 0,
                    fatura_turu: values.faturaTuru,
                    banka_kodu: values.bankaKodu || bankCode || '',
                    ...(onaylar.length ? { onaylar } : {})
                })
            });
            const body = await response.json().catch(() => ({}));
            const det = body?.detail;
            const kod = response.status === 409 && det && typeof det === 'object'
                ? (det.kod || (det.cakisanlar ? 'mukerrer' : null)) : null;
            if (kod && !onaylar.includes(kod)) {
                Modal.confirm({
                    title: det.message || 'Onay gerekli',
                    width: 640,
                    content: (
                        <ul style={{ paddingLeft: 18, margin: 0 }}>
                            {(det.cakisanlar || []).map((c) => (
                                <li key={c.id}>
                                    {`${c.evrakNo || '(evraksız)'} · ${formatDate(c.donemBaslangic)} - ${formatDate(c.donemBitis)} · ${formatCurrency(c.fatura_tutar)} · ${c.durum} (#${c.id})`}
                                </li>
                            ))}
                            {(det.sorunlar || []).map((x) => <li key={x}>{x}</li>)}
                        </ul>
                    ),
                    okText: 'Yine de oluştur',
                    okButtonProps: { danger: true },
                    cancelText: 'Vazgeç',
                    onOk: () => handleSubmit(values, [...onaylar, kod])
                });
                return;
            }
            if (!response.ok) {
                const det = body?.detail;
                throw new Error(typeof det === 'string' ? det : (det?.message || `HTTP ${response.status}`));
            }
            message.success(`Elektrik fatura talebi oluşturuldu (#${body?.data?.id})`);
        } catch (err) {
            console.error('Submit error:', err);
            message.error('Kaydetme sırasında hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !data) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '50vh',
                background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)'
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert
                    message="Hata"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert
                    message="Veri Bulunamadı"
                    description="Elektrik fatura işlemi için gerekli veriler yüklenemedi."
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div className={`elektrik-fatura-page ${darkMode ? 'dark-theme' : ''}`} 
             style={{ 
                 padding: '20px',
                 background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)',
                 minHeight: '100vh'
             }}>
            <Card 
                className={darkMode ? 'dark-card' : ''}
                style={{
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Title level={2} style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                    {`${data.Lokasyon} | ${data['Kabin No']}. Kabin ${data.Banka || data.banka_adi || data.banka || 'Bilinmiyor'} Elektrik Fatura Talebi`}
                </Title>
                
                <Divider />

                <Row gutter={[24, 24]}>
                    {/* Information Table */}
                    <Col xs={24} lg={10}>
                        <Card 
                            title="Lokasyon Bilgileri"
                            size="small"
                            className={darkMode ? 'dark-card' : ''}
                            style={{
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                                borderColor: darkMode ? 'var(--dark-border)' : undefined
                            }}
                        >
                            <Table
                                rowKey="key"
                                columns={[
                                    { title: '', dataIndex: 'key', key: 'k' },
                                    { title: '', dataIndex: 'value', key: 'v' }
                                ]}
                                dataSource={[
                                    { key: 'İl / İlçe', value: `${data.İl || 'Bilinmiyor'} / ${data.İlçe || 'Bilinmiyor'}` },
                                    { key: 'Lokasyon Durumu', value: locationStatusOptions[data.durum] || locationStatusOptions[data.lokasyon_durum] || 'Bilinmiyor' },
                                    { key: '2019 Elektrik Tutarı', 
                                      value: formatCurrency(data['2019_elektrik'] || 0) 
                                    },
                                    { key: 'Artış Şekli / Şekli', value: 'Yok' },
                                    { key: 'Kira Başlangıç Tarihi', value: data.kiraBaslangicTarihi ? formatDate(data.kiraBaslangicTarihi) : 'Bilinmiyor' },
                                    ...(data['Söz. Baş. Tar.'] && data['Söz. Bit. Tar.'] ? [{
                                        key: 'Sözleşme Başlangıç / Bitiş Tarihi | İmza Durumu',
                                        value: `${formatDate(data['Söz. Baş. Tar.'])} / ${formatDate(data['Söz. Bit. Tar.'])} (${calculateDateDifference(data['Söz. Baş. Tar.'], data['Söz. Bit. Tar.'])}) | ${contractSignatureStatusOptions[data.contract_imza_durumu] || 'Bilinmiyor'}`
                                    }] : []),
                                    ...(data['ys_bitis'] ? [{
                                        key: 'Ys. Sözleşme Bitiş Tarihi | İmza Durumu',
                                        value: `${formatDate(data['ys_bitis'])} (${calculateDateDifference(data['ys_bitis'], dayjs().format('YYYY-MM-DD'))}) | ${contractSignatureStatusOptions[data['ys_imza_durumu']] || 'Bilinmiyor'}`
                                    }] : []),
                                    { key: 'Temlik Durumu', value: contractData?.temlik ? 'Var' : 'Yok' }
                                ]}
                                pagination={false}
                                showHeader={false}
                                size="small"
                                className={darkMode ? 'dark-table' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                }}
                            />
                        </Card>
                    </Col>

                    {/* Form */}
                    <Col xs={24} lg={14}>
                        <Card 
                            title="Fatura Bilgileri"
                            size="small"
                            className={darkMode ? 'dark-card' : ''}
                            style={{
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                                borderColor: darkMode ? 'var(--dark-border)' : undefined
                            }}
                        >
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={(values) => handleSubmit(values)}
                                className={darkMode ? 'dark-form' : ''}
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="Fatura Dönemi Başlangıç"
                                            name="donemBaslangic"
                                            rules={[{ required: true, message: 'Başlangıç tarihi gerekli' }]}
                                        >
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                className={darkMode ? 'dark-date-picker' : ''}
                                                placeholder="Başlangıç tarihi seçin"
                                                format="DD-MM-YYYY"
                                            />
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="Fatura Dönemi Bitiş"
                                            name="donemBitis"
                                            rules={[{ required: true, message: 'Bitiş tarihi gerekli' }]}
                                        >
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                className={darkMode ? 'dark-date-picker' : ''}
                                                placeholder="Bitiş tarihi seçin"
                                                format="DD-MM-YYYY"                                            
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    label="Fatura Tutarı (KDV Hariç)"
                                    name="fatura_tutar"
                                    rules={[{ required: true, message: 'Fatura tutarı gerekli' }]}
                                >
                                    <Input
                                        type="number"
                                        step="any"
                                        placeholder="Fatura tutarı"
                                        className={darkMode ? 'dark-input' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Banka Kodu"
                                    name="bankaKodu"
                                >
                                    <Input
                                        placeholder="Banka Kodu"
                                        className={darkMode ? 'dark-input' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    />
                                </Form.Item>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="Fatura Türü"
                                            name="faturaTuru"
                                            rules={[{ required: true, message: 'Fatura türü gerekli' }]}
                                        >
                                            <Select
                                                className={darkMode ? 'dark-select' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                {invoiceTypeOptions.map(option => (
                                                    <Option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label="Firma"
                                            name="firma"
                                            rules={[{ required: true, message: 'Firma seçimi gerekli' }]}
                                        >
                                            <Select
                                                className={darkMode ? 'dark-select' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                {companyOptions.map(option => (
                                                    <Option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item>
                                    <Space>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SaveOutlined />}
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                            style={{
                                                backgroundColor: darkMode ? 'var(--dark-primary)' : undefined,
                                                borderColor: darkMode ? 'var(--dark-primary)' : undefined
                                            }}
                                        >
                                            Kaydet
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default ElektrikFaturaIslemPage;
